import React from 'react';
import { Save, Check, RefreshCcw } from 'lucide-react';
import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

// Single analyzed item card: manual name, estimated value/distribution,
// confidence, editable actual price and condition / AI-source badges.
const ResultItemCard = ({
  item,
  index,
  originalPrices,
  originalNames,
  updatingId,
  updatingNameId,
  onPriceChange,
  onManualNameChange,
  onSavePrice,
  onSaveItemName,
}) => {
  const isDirty = item.id && originalPrices[item.id] !== undefined && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
  const isUpdating = updatingId === item.id;
  const isNameDirty = item.id && originalNames[item.id] !== undefined && originalNames[item.id] !== (item.manual_name || '');
  const isNameUpdating = updatingNameId === item.id;
  const condition = item?.condition || 'Unknown';
  const priceSource = item?.price_source || 'model_knowledge';
  const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
  const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
  const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';

  return (
    <div className="bg-white rounded border border-slate-200 p-3 shadow-sm">
      <strong className="block text-slate-700 text-base mb-1">{item.item || `Item ${index + 1}`}</strong>

      <div className="flex flex-col gap-1 mb-2">
        <span className="text-slate-500 text-xs">Manual Name:</span>
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Enter custom name..."
            className={`w-full pr-8 py-1 px-2 border rounded text-sm text-slate-700 focus:outline-none focus:ring-1 transition-all ${
              isNameDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
            }`}
            value={item.manual_name || ''}
            onChange={(e) => onManualNameChange(item.id, e.target.value)}
          />
          {item.id && (
            <button
              onClick={() => onSaveItemName(item.id, item.manual_name || '')}
              disabled={!isNameDirty || isNameUpdating}
              className={`absolute right-1 p-1 rounded transition-all ${
                isNameUpdating ? 'text-slate-400' : isNameDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default'
              }`}
            >
              {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isNameDirty ? <Save size={14} /> : <Check size={14} />}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm text-slate-600">
          Estimated Value: <span className="font-bold text-green-600">{Number.isFinite(Number(item.estimated_value)) ? `$${Number(item.estimated_value).toFixed(2)}` : 'n/a'}</span>
        </p>
        <PriceDistributionView
          rangeValue={item?.estimated_value_range}
          distributionValue={item?.price_estimate_distribution}
        />
        <p className="text-sm text-slate-600">
          Confidence: <span className="font-bold text-indigo-600">{formatConfidencePercent(item?.confidence_score)}</span>
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Actual Price:</span>
          <div className="relative flex items-center">
            <span className="absolute left-2 text-slate-400 text-sm">$</span>
            <input
              type="number"
              placeholder="0.00"
              value={item.actual_price}
              onChange={(e) => onPriceChange(item.id, e.target.value)}
              className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                isDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
              }`}
              step="0.01"
            />
            {item.id && (
              <button
                onClick={() => onSavePrice(item.id, item.actual_price)}
                disabled={!isDirty || isUpdating}
                className={`absolute right-1 p-1 rounded transition-all ${
                  isUpdating ? 'text-slate-400' : isDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default'
                }`}
              >
                {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isDirty ? <Save size={14} /> : <Check size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <span className="text-[9px] text-slate-400">Condition:</span>
        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
        <span className="text-[9px] text-slate-400 ml-1">AI Src:</span>
        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
      </div>

      {item.explanation && (
        <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 leading-relaxed">
          {item.explanation}
        </div>
      )}
    </div>
  );
};

export default ResultItemCard;
